import Vue from "vue";
import { ItemObject, TreeNode } from "@/components/Customizer/StandardNavigation/StandardNavigation.t";

export default Vue.extend({
	name: "StandardNavigationMixin",
	props: {
		itemsElement: {
			type: String,
			required: false,
			default: "li",
		},
		itemsWrapperElement: {
			type: String,
			required: false,
			default: "ul",
		},
		contentElement: {
			type: String,
			required: false,
			default: "button",
		},
		multipleSameLevelNodesOpened: {
			type: Boolean,
			default: true,
			required: false,
		},
		leafIconContent: {
			type: String,
			required: false,
			default: null,
		},
		closedGroupIconContent: {
			type: String,
			required: false,
			default: null,
		},
		openGroupIconContent: {
			type: String,
			required: false,
			default: null,
		},
		useBullets: {
			type: Boolean,
			required: false,
			default: true,
		},
		activateOnCollapse: {
			type: Boolean,
			required: false,
			default: true,
		},
		updateExpandedFlagOnNavigation: {
			type: Boolean,
			default: true,
			required: false,
		},
	},
	computed: {
		hasChildren(): boolean {
			// @ts-ignore
			return !!(this.item && this.item.children && this.item.children.length);
		},
		id(): number | string | boolean {
			// @ts-ignore
			if (this.item && this.item.id) return this.item.id;
			return false;
		}
	},
	methods: {
		/**
		 * This method is executed when an item is activated, it passes the event
		 * above the tree until the root where it's handled
		 */
		onTreeItemActivated(parameters: TreeNode): void {
			let aux: TreeNode;
			// @ts-ignore
			if (this.item) {
				// @ts-ignore
				const path = [this.item.id].concat(parameters.path);
				aux = { ...parameters, path: path };
			} else {
				aux = { ...parameters };
			}
			this.$emit.apply(this, ["tree-item-activated", aux]);
		},
		/**
		 * Sets the focus to the label of the root item of this subtree
		 */
		getFocus(): void {
			this.$emit("tree-item-received-focus", this);
			if (this.hasChildren) {
				(this.$refs.hasChildrenTextLabel as any).focus();
			} else {
				if ((this.$refs.standAloneTextLabel as any)){
					(this.$refs.standAloneTextLabel as any).focus();
				}
			}
		},
		/**
		* Handles keyboard input
		*/
		onTreeItemKeyDown(item: ItemObject, event: KeyboardEvent): void {
			// @ts-ignore
			const siblings = this.items || this.item.children;
			const currentFocusPos = siblings.indexOf(item);
			let nextFocusPos: number = -1;
			let focusParentsNextSibling: boolean = false;
			let focusOnItemsFirstChild: boolean = false;
			let focusOnParent: boolean = false;
			let focusParentsLastSibling: boolean = false;
			let viewItem;
			switch (event.key) {
				case 'Home':
					this.$emit("tree-item-go-to-first");
					break;
				case "End":
					this.$emit("tree-item-go-to-last");
					break;
				case "ArrowUp":
					if (currentFocusPos === 0) {
						focusOnParent = true;
					} else if (
						(this.$refs[`standard-navigation-item-${currentFocusPos - 1}`] as any) &&
						(this.$refs[`standard-navigation-item-${currentFocusPos - 1}`] as any)[0] &&
						(this.$refs[`standard-navigation-item-${currentFocusPos - 1}`] as any)[0]
							.hasChildren &&
						(this.$refs[`standard-navigation-item-${currentFocusPos - 1}`] as any)[0].isExpanded
					) {
						focusParentsLastSibling = true;
					} else {
						nextFocusPos = currentFocusPos - 1
					};
					break;
				case "ArrowDown":
					if (
						(this.$refs[`standard-navigation-item-${currentFocusPos}`] as any) &&
						(this.$refs[`standard-navigation-item-${currentFocusPos}`] as any)[0] &&
						(this.$refs[`standard-navigation-item-${currentFocusPos}`] as any)[0].hasChildren &&
						(this.$refs[`standard-navigation-item-${currentFocusPos}`] as any)[0].isExpanded
					) {
						focusOnItemsFirstChild = true;
					} else if (currentFocusPos === siblings.length - 1) {
						focusParentsNextSibling = true;
					} else nextFocusPos = currentFocusPos + 1;
					break;
				case "ArrowRight":
					viewItem = (this.$refs[`standard-navigation-item-${currentFocusPos}`] as any)[0];
					if (!viewItem.isExpanded && viewItem.hasChildren) {
						this.onCollapseSiblings(viewItem);
						if (this.updateExpandedFlagOnNavigation) viewItem.isExpanded = true;
						this.$emit("tree-item-expand-item", siblings[currentFocusPos]);
					} else if (viewItem.isExpanded) {
						if (
							viewItem.$refs["standard-navigation-item-0"] &&
							viewItem.$refs["standard-navigation-item-0"][0]
						) {
							viewItem.$refs["standard-navigation-item-0"][0].getFocus();
						}
					}
					break;
				case "ArrowLeft":
					viewItem = (this.$refs[`standard-navigation-item-${currentFocusPos}`] as any)[0];
					if (viewItem.isExpanded && viewItem.hasChildren) {
						if (this.updateExpandedFlagOnNavigation)
							viewItem.isExpanded = false;
						this.$emit("tree-item-collapse-item", siblings[currentFocusPos]);
					} else if (!viewItem.isExpanded) {
						viewItem.$emit("tree-item-focus-parent");
					}
					break;
				case "*":
					for (let i = 0; i < siblings.length; i++) {
						viewItem = (this.$refs[`standard-navigation-item-${i}`] as any)[0];
						if (viewItem.hasChildren && !viewItem.isExpanded) {
							if (this.updateExpandedFlagOnNavigation)
								viewItem.isExpanded = true;
							this.$emit("tree-item-expand-item", siblings[i]);
						}
					}
					break;
			}
			if (
				!focusParentsNextSibling &&
				!focusOnItemsFirstChild &&
				!focusOnParent &&
				!focusParentsLastSibling &&
				nextFocusPos >= 0
			) {
				(this.$refs[`standard-navigation-item-${nextFocusPos}`] as any)[0].getFocus();
			}

			if (focusOnItemsFirstChild) {
				(this.$refs[`standard-navigation-item-${currentFocusPos}`] as any)[0].focusOnFirstChild();
			}

			if (focusParentsNextSibling) {
				this.$emit("tree-item-on-focus-next-item");
			}

			if (focusOnParent) {
				(this.$refs[`standard-navigation-item-${currentFocusPos}`] as any)[0].$emit(
					"tree-item-focus-parent"
				);
			}
			if (focusParentsLastSibling) {
				(this.$refs[`standard-navigation-item-${currentFocusPos}`] as any)[0].$emit(
					"tree-item-on-focus-previous-item"
				);
			}
		},
		/**
		* Collapses all children items but the one passed as argument
		*/
		onCollapseSiblings(expandedItem: ItemObject): void {
			if (!this.updateExpandedFlagOnNavigation) return;
			// @ts-ignore
			const siblings = this.items || this.item.children;
			// @ts-ignore
			siblings.forEach((item) => {
				if (item !== expandedItem) {
					const pos = siblings.indexOf(item);
					(this.$refs[`standard-navigation-item-${pos}`] as any)[0].collapse();
				}
			});
		},
		/**
	   * Sets the focus on the first child of the tree item
	   */
		focusOnFirstChild(): void {
			const treeItem =
				this.$refs["standard-navigation-item-0"] && (this.$refs["standard-navigation-item-0"] as any)[0];
			if (treeItem) treeItem.getFocus();
		},
		/**
		 * Focus on item's previous sibling last child
		 * For reference purposes
		 * - previous sibling
		 *   - previous sibling first child
		 *   - previous sibling last child -> focus
		 * - current item
		 * @param item
		 */
		onFocusPreviousItem(item: ItemObject): void {
			// @ts-ignore
			const siblings = this.items || this.item.children;
			const currentItemIndex = siblings.indexOf(item);
			const previousItemIndex = currentItemIndex - 1;
			if (previousItemIndex >= 0) {
				(this.$refs[
					`standard-navigation-item-${previousItemIndex}`
				] as any)[0].focusLastExpandedDescendantItem();
			}
		},
		/**
		* Sets the focus on the last descendant leaf/expanded item of this subtree
		* For reference:
		* - current item
		*   - child item
		*   - child item
		*     - last expanded descendant item
		*/
		focusLastExpandedDescendantItem(): void {
			// @ts-ignore
			const children = this.items || this.item.children;
			// @ts-ignore
			if (!this.hasChildren || !this.isExpanded) {
				this.getFocus();
			} else {
				(this.$refs[
					`standard-navigation-item-${children.length - 1}`
				] as any)[0].focusLastExpandedDescendantItem();
			}
		},
		/**
		 * Sets the focus on the item's next sibling or on the next sibling of the
		 * first ancestor that has a next sibling
		 */
		onFocusNextItem(item: ItemObject): void {
			// @ts-ignore
			const siblings = this.items || this.item.children;
			const currentItemIndex = siblings.indexOf(item);
			const nextItemIndex = currentItemIndex + 1;
			// @ts-ignore
			if (nextItemIndex >= siblings.length && this.item) {
				// @ts-ignore
				this.$emit("tree-item-on-focus-next-item", this.item);
			} else {
				if (
					nextItemIndex >= 0 &&
					this.$refs[`standard-navigation-item-${nextItemIndex}`] &&
					(this.$refs[`standard-navigation-item-${nextItemIndex}`] as any)[0]
				) {
					(this.$refs[`standard-navigation-item-${nextItemIndex}`] as any)[0].getFocus();
				}
			}
		},
		onTreeItemExpandItem(item: ItemObject): void {
			this.$emit("tree-item-expand-item", item);
		},
		onTreeItemCollapseItem(item: ItemObject): void {
			this.$emit("tree-item-collapse-item", item);
		},
		onTreeItemGoToFirst(): void {
			this.$emit("tree-item-go-to-first");
		},
		onTreeItemGoToLast(): void {
			this.$emit("tree-item-go-to-last");
		},
	}
});
